import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MigrationReport, MigrationError, LegacySection } from '../types/models';
import { useAppContext } from '../context/RevisionContext';
import { RevisionSystemConfig } from '../config/RevisionSystemConfig';
import './MigrationTool.css';

/**
 * MigrationTool component
 * Allows users to migrate legacy revision cards from Firestore to the new system
 * 
 * Features:
 * - Start migration button
 * - Real-time progress display (sections processed, cards migrated)
 * - Migration status messages
 * - Error handling that continues processing
 * - MigrationReport display on completion
 * - Configurable section filtering based on system type
 * 
 * Requirements: 2.1, 7.2, 10.2
 */

interface MigrationToolProps {
  config: RevisionSystemConfig;
  onMigrationComplete?: (report: MigrationReport) => void;
}

type MigrationStatus = 'idle' | 'running' | 'completed' | 'error';

const MigrationTool: React.FC<MigrationToolProps> = ({ config, onMigrationComplete }) => {
  const navigate = useNavigate();
  const { user, refreshSections, firestoreService } = useAppContext();
  
  const [status, setStatus] = useState<MigrationStatus>('idle');
  const [progress, setProgress] = useState({
    sectionsProcessed: 0,
    cardsMigrated: 0,
    currentSection: ''
  });
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');

  /**
   * Starts the migration process
   * Finds legacy sections, migrates cards, handles errors gracefully
   */
  const startMigration = async () => {
    if (!user?.uid) {
      setStatus('error');
      setStatusMessage('User not authenticated. Please log in.');
      return;
    }

    setStatus('running');
    setStatusMessage('Starting migration...');
    const startTime = Date.now();
    
    const errors: MigrationError[] = [];
    let sectionsFound = 0;
    let sectionsMigrated = 0;
    let cardsFound = 0;
    let cardsMigrated = 0;

    try {
      // Find all legacy sections from the configured legacy collection
      setStatusMessage('Searching for legacy sections...');
      const allLegacySections: LegacySection[] = await firestoreService.findLegacySections();
      
      // Filter sections using the config's sectionNameFilter
      const legacySections = allLegacySections.filter(section => 
        config.migration.sectionNameFilter(section.title)
      );
      
      sectionsFound = legacySections.length;
      
      if (sectionsFound === 0) {
        setStatusMessage('No legacy sections found matching the filter criteria.');
        const emptyReport: MigrationReport = {
          sectionsFound: 0,
          sectionsMigrated: 0,
          cardsFound: 0,
          cardsMigrated: 0,
          errors: [],
          duration: Date.now() - startTime
        };
        setReport(emptyReport);
        setStatus('completed');
        if (onMigrationComplete) {
          onMigrationComplete(emptyReport);
        }
        return;
      }

      setStatusMessage(`Found ${sectionsFound} legacy section(s). Starting migration...`);

      // Process each legacy section
      for (const legacySection of legacySections) {
        try {
          setProgress(prev => ({
            ...prev,
            currentSection: legacySection.title
          }));
          setStatusMessage(`Migrating section: ${legacySection.title}`);

          // Create or find the section in the new system
          // Preserve the section name (e.g., "Greek [vocab]")
          let targetSectionId: string;
          
          try {
            // Check if section already exists
            const existingSections = await firestoreService.getAllSections();
            const existingSection = existingSections.find(s => s.name === legacySection.title);
            
            if (existingSection) {
              targetSectionId = existingSection.id;
              setStatusMessage(`Using existing section: ${legacySection.title}`);
            } else {
              // Create new section
              const newSection = await firestoreService.createSection(legacySection.title);
              targetSectionId = newSection.id;
              setStatusMessage(`Created new section: ${legacySection.title}`);
            }
          } catch (sectionError) {
            const error: MigrationError = {
              sectionId: legacySection.id,
              error: `Failed to create/find section: ${sectionError instanceof Error ? sectionError.message : 'Unknown error'}`,
              timestamp: new Date()
            };
            errors.push(error);
            console.error('Section creation error:', error);
            continue; // Skip this section and continue with others
          }

          // Migrate cards from this section
          const sectionCardsFound = legacySection.cards.length;
          cardsFound += sectionCardsFound;
          let sectionCardsMigrated = 0;

          for (const legacyCard of legacySection.cards) {
            try {
              await firestoreService.migrateLegacyCard(legacyCard, targetSectionId);
              sectionCardsMigrated++;
              cardsMigrated++;
              
              // Update progress display
              setProgress(prev => ({
                ...prev,
                cardsMigrated: cardsMigrated
              }));
            } catch (cardError) {
              // Log error but continue with remaining cards
              const error: MigrationError = {
                cardId: legacyCard.id,
                sectionId: legacySection.id,
                error: `Failed to migrate card: ${cardError instanceof Error ? cardError.message : 'Unknown error'}`,
                timestamp: new Date()
              };
              errors.push(error);
              console.error('Card migration error:', error);
            }
          }

          setStatusMessage(`Migrated ${sectionCardsMigrated}/${sectionCardsFound} cards from ${legacySection.title}`);
          sectionsMigrated++;
          
          // Update sections processed
          setProgress(prev => ({
            ...prev,
            sectionsProcessed: sectionsMigrated
          }));

        } catch (sectionError) {
          // Log section-level error and continue
          const error: MigrationError = {
            sectionId: legacySection.id,
            error: `Failed to process section: ${sectionError instanceof Error ? sectionError.message : 'Unknown error'}`,
            timestamp: new Date()
          };
          errors.push(error);
          console.error('Section processing error:', error);
        }
      }

      // Generate migration report
      const duration = Date.now() - startTime;
      const migrationReport: MigrationReport = {
        sectionsFound,
        sectionsMigrated,
        cardsFound,
        cardsMigrated,
        errors,
        duration
      };

      setReport(migrationReport);
      setStatus('completed');
      setStatusMessage('Migration completed!');

      // Refresh sections to show newly migrated data
      await refreshSections();

      // Call completion callback
      if (onMigrationComplete) {
        onMigrationComplete(migrationReport);
      }

    } catch (error) {
      // Critical error that stops migration
      console.error('Critical migration error:', error);
      setStatus('error');
      setStatusMessage(`Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      const duration = Date.now() - startTime;
      const errorReport: MigrationReport = {
        sectionsFound,
        sectionsMigrated,
        cardsFound,
        cardsMigrated,
        errors: [
          ...errors,
          {
            error: `Critical error: ${error instanceof Error ? error.message : 'Unknown error'}`,
            timestamp: new Date()
          }
        ],
        duration
      };
      setReport(errorReport);
    }
  };

  /**
   * Resets the migration tool to initial state
   */
  const resetMigration = () => {
    setStatus('idle');
    setProgress({
      sectionsProcessed: 0,
      cardsMigrated: 0,
      currentSection: ''
    });
    setReport(null);
    setStatusMessage('');
  };

  return (
    <div className="migration-tool">
      <div className="migration-header">
        <h2>Legacy Card Migration</h2>
        <p>Migrate your existing revision cards from Firestore to the new system.</p>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate(config.routing.basePath)}
          style={{ marginTop: '10px' }}
        >
          Back to Dashboard
        </button>
      </div>

      {status === 'idle' && (
        <div className="migration-start">
          <button 
            className="btn-primary btn-start-migration"
            onClick={startMigration}
          >
            Start Migration
          </button>
          <p className="migration-info">
            This will search for legacy sections and migrate all matching cards to the new system.
          </p>
        </div>
      )}

      {status === 'running' && (
        <div className="migration-progress">
          <div className="progress-header">
            <h3>Migration in Progress</h3>
            <div className="spinner"></div>
          </div>
          
          <div className="progress-stats">
            <div className="progress-stat">
              <span className="progress-label">Sections Processed:</span>
              <span className="progress-value">{progress.sectionsProcessed}</span>
            </div>
            <div className="progress-stat">
              <span className="progress-label">Cards Migrated:</span>
              <span className="progress-value">{progress.cardsMigrated}</span>
            </div>
          </div>

          {progress.currentSection && (
            <div className="current-section">
              <span className="current-label">Current Section:</span>
              <span className="current-value">{progress.currentSection}</span>
            </div>
          )}

          <div className="status-message">
            {statusMessage}
          </div>
        </div>
      )}

      {(status === 'completed' || status === 'error') && report && (
        <div className="migration-report">
          <div className="report-header">
            <h3>Migration Report</h3>
            {status === 'completed' && (
              <span className="status-badge status-success">Completed</span>
            )}
            {status === 'error' && (
              <span className="status-badge status-error">Error</span>
            )}
          </div>

          <div className="report-summary">
            <div className="report-stat">
              <span className="report-label">Sections Found:</span>
              <span className="report-value">{report.sectionsFound}</span>
            </div>
            <div className="report-stat">
              <span className="report-label">Sections Migrated:</span>
              <span className="report-value">{report.sectionsMigrated}</span>
            </div>
            <div className="report-stat">
              <span className="report-label">Cards Found:</span>
              <span className="report-value">{report.cardsFound}</span>
            </div>
            <div className="report-stat">
              <span className="report-label">Cards Migrated:</span>
              <span className="report-value">{report.cardsMigrated}</span>
            </div>
            <div className="report-stat">
              <span className="report-label">Duration:</span>
              <span className="report-value">{(report.duration / 1000).toFixed(2)}s</span>
            </div>
          </div>

          {report.errors.length > 0 && (
            <div className="report-errors">
              <h4>Errors ({report.errors.length})</h4>
              <div className="error-list">
                {report.errors.map((error, index) => (
                  <div key={index} className="error-item">
                    <div className="error-timestamp">
                      {error.timestamp.toLocaleTimeString()}
                    </div>
                    <div className="error-message">{error.error}</div>
                    {error.cardId && (
                      <div className="error-detail">Card ID: {error.cardId}</div>
                    )}
                    {error.sectionId && (
                      <div className="error-detail">Section ID: {error.sectionId}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="report-actions">
            <button 
              className="btn-secondary"
              onClick={resetMigration}
            >
              Reset
            </button>
            <button 
              className="btn-primary"
              onClick={() => navigate(config.routing.basePath)}
              style={{ marginLeft: '10px' }}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MigrationTool;
